import React, { useEffect } from 'react';
import {
  Font,
  StyleSheet,
  Text,
  View,
} from '@react-pdf/renderer';

import _ from 'lodash';

import { OrganizationBrandData } from '../../../_libs/app/types';
import { ReportDataType } from '../../../_libs/data/types';
import { DataBlurbType } from '../../../molecules/data/DataBlurb';
import { DataBarGraphType } from '../../../molecules/data/DataBarGraph';
import { DataOneLinerType } from '../../../molecules/data/DataOneLiner';

import GeneralPageLayout from '../pages/GeneralPageLayout';

import radiusToText from '../utils/radiusToText';
import removeHyphenation from '../utils/removeHyphenation';

removeHyphenation(Font);

export interface DataOneLinerPrintType extends DataOneLinerType {
  primaryBrand: OrganizationBrandData;
}
const DataOneLinerPrint = ({
  label,
  value,
  primaryBrand,
}: DataOneLinerPrintType): JSX. Element => {
  return (
    <View>
      <Text style={{
        fontFamily: 'Courier-Bold',
        fontSize: 10.5,
        color: primaryBrand.colors.dark,
      }}>{label}<Text style={{
        fontFamily: 'Courier',
      }}> · {value}</Text></Text>
    </View>
  );
};

export interface DataBlurbPrintType extends DataBlurbType {
  primaryBrand: OrganizationBrandData;
}
const DataBlurbPrint = ({
  value,
  label,
  primaryBrand,
}: DataBlurbPrintType): JSX.Element => {

  return (
    <View>
      <View>
        <Text
          style={{
            fontFamily: 'Courier',
            fontSize: 10.5,
            color: primaryBrand.colors.dark,
          }}
        >
          {label}
        </Text>
      </View>
      <View>
        <Text
          style={{
            fontFamily: 'Helvetica-Bold',
            fontSize: 36,
            color: primaryBrand.colors.dark,
          }}
        >
          {value}
        </Text>
      </View>
    </View>
  );
};

export interface DataBarGraphPrintType extends DataBarGraphType {
  primaryBrand: OrganizationBrandData;
}
const DataBarGraphPrint = ({
  label,
  percent,
  value = null,
  primaryBrand,
}: DataBarGraphPrintType): JSX.Element => {

  const Styles = StyleSheet.create({
    onelinerView: {
      width: '100%',
      height: 15,
    },
    graphView: {
      width: '100%',
      height: 15,
      borderRadius: 3,
      position: 'relative',
      overflow: 'hidden',
      marginBottom: 8,
    },
    foregroundView: {
      width: `${(isNaN(percent as number)) ? 0 : percent}%`,
      height: 15,
      position: 'absolute',
      top: 0,
      left: 0,
      backgroundColor: primaryBrand.colors.primary,
    },
    backgroundView: {
      width: '100%',
      height: 15,
      position: 'absolute',
      top: 0,
      left: 0,
      backgroundColor: primaryBrand.colors.light,
    },
  });

  const _value = (value !== null) ? value.toFixed(0) : (parseFloat(`${percent}`).toFixed(2) + '%');

  return (
    <View>
      <View style={Styles.onelinerView}>
        <DataOneLinerPrint 
          label={label}
          value={_value}
          primaryBrand={primaryBrand}
        />
      </View>
      <View style={Styles.graphView}>
        <View style={Styles.backgroundView}></View>
        <View style={Styles.foregroundView}></View>
      </View>
    </View>
  );
};

interface DataPageType {
  primaryBrand: OrganizationBrandData;
  reportData: ReportDataType;
  themeData: any;
  topThemes: any;
  radius: number;
  zipCode: string;
  uuid: string;
}
const DataPage = ({
  primaryBrand,
  themeData,
  topThemes,
  reportData,
  radius,
  zipCode,
  uuid,
}: DataPageType): JSX.Element => {
  const data = reportData;
  const Styles = StyleSheet.create({
    sectionHeader: {
      fontFamily: 'Helvetica-Bold',
      textTransform: 'uppercase',
      letterSpacing: '1px',
      fontSize: 8,
      marginBottom: 9,
    },
  });

  useEffect(() => {
    const _TopThemes: JSX.Element[] = [];
    if (themeData && topThemes) {
      _.map(topThemes, (theme, i) => {
        const relevantThemeData = _.find(themeData, { id: theme.themeId });
        _TopThemes.push(
          <View
            key={i}
            style={{
              width: '100%',
              backgroundColor: primaryBrand.colors.light,
              borderRadius: 4,
              padding: 9,
              marginBottom: 18,
            }}>
            <DataBlurbPrint
              primaryBrand={primaryBrand}
              label={`Theme #${i + 1}: ${relevantThemeData.name}`}
              value={(theme.prevalenceCount / reportData.count * 100).toFixed(2) + '%'} />
            <Text style={{ 
              fontFamily: 'Courier',
              fontSize: 10.5,
              color: primaryBrand.colors.dark,
            }}>
              (n = {theme.prevalenceCount})
            </Text>
          </View>
        );
        return;
      });
    }
  }, [themeData, topThemes]);

  // FIX ME: This may cause React FE to stop working
  const _TopThemes = [];
  _.map(topThemes, (theme, i) => {
    const relevantThemeData = _.find(themeData, { id: theme.themeId });
    _TopThemes.push(
      <View
        key={i}
        style={{
          width: '100%',
          backgroundColor: primaryBrand.colors.light,
          borderRadius: 4,
          padding: 9,
          marginBottom: 18,
        }}>
        <DataBlurbPrint
          primaryBrand={primaryBrand}
          label={`Theme #${i + 1}: ${relevantThemeData.name}`}
          value={(theme.prevalenceCount / reportData.count * 100).toFixed(2) + '%'} />
        <Text style={{ 
          fontFamily: 'Courier',
          fontSize: 10.5,
          color: primaryBrand.colors.dark,
        }}>
          (n = {theme.prevalenceCount})
        </Text>
      </View>
    );
  });

  return (
    <>
      <GeneralPageLayout
        sectionTypeText="Community Insights"
        sectionTitleText="Community Characteristics"
        footerText={uuid}
        textColor={primaryBrand.colors.dark}
        backgroundColor="white"
        bookmark="Community Insights"
      >
        <View
          style={{
            border: '1px solid rgb(217, 232, 242)',
            padding: 14,
            borderRadius: 4,
            marginBottom: 18,
          }}
        >
          <Text style={Styles.sectionHeader}>Geography Included</Text>
          <View style={{
            display: 'flex',
            flexDirection: 'row',
          }}>
            <View style={{
              width: '100%',
            }}>
              <DataBlurbPrint
                primaryBrand={primaryBrand}
                value={zipCode}
                label="ZIP Code"
              />
            </View>
            <View style={{
              width: '100%',
            }}>
              <DataBlurbPrint
                primaryBrand={primaryBrand}
                value={`${radius} ${(radius > 1) ? 'miles' : 'mile'}`}
                label="Radius"
              />
            </View>
          </View>
        </View>
        <View style={{
          border: '1px solid rgb(217, 232, 242)',
          borderRadius: 4,
          padding: 14,
          marginBottom: 18,
        }}>
          <Text style={Styles.sectionHeader}>Neighborhood Statistics</Text>
          <View style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            marginBottom: 18,
          }}>
            <DataBlurbPrint
              primaryBrand={primaryBrand}
              value={Intl.NumberFormat('en-US').format(data.count)}
              label="Block Groups"
            />
            <DataBlurbPrint
              primaryBrand={primaryBrand}
              value={Intl.NumberFormat('en-US').format(data.household_est)}
              label="Estimated Households"
            />
            <DataBlurbPrint
              primaryBrand={primaryBrand}
              value={Intl.NumberFormat('en-US').format(data.totalpopulation_est)}
              label="Estimated Population"
            />
          </View>
          <View style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
          }}>
            <DataBlurbPrint
              primaryBrand={primaryBrand}
              value={data.eqi_land_walkability_value.toFixed(2)}
              label="Walkability Index"
              risk={data.eqi_land_walkability_label}
            />
            <DataBlurbPrint
              primaryBrand={primaryBrand}
              value={(data.poverty_belowpoverty_value * 100).toFixed(2) + '%'}
              label="Poverty Rate"
              risk={data.poverty_belowpoverty_label}
            />
            <DataBlurbPrint
              primaryBrand={primaryBrand}
              value={data.ep_uninsur_value.toFixed(2) + '%'}
              label="Uninsured"
              risk={data.ep_uninsur_label}
            />
          </View>
        </View>
        <View style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          marginBottom: 18,
        }}>
          <View style={{
            border: '1px solid rgb(217, 232, 242)',
            borderRadius: 4,
            padding: 14,
            width: '100%',
            marginRight: 9,
          }}>
            <Text style={Styles.sectionHeader}>Reported Age</Text>
            <DataBarGraphPrint
              primaryBrand={primaryBrand}
              percent={parseFloat(data.ep_age17_value.toFixed(2))}
              label="17 or Younger"
              risk={data.ep_age17_label}
            />
            <DataBarGraphPrint
              primaryBrand={primaryBrand}
              percent={parseFloat((100 - (data.ep_age17_value + data.ep_age65_value)).toFixed(2))}
              label="18-64"
            />
            <DataBarGraphPrint
              primaryBrand={primaryBrand}
              percent={parseFloat(data.ep_age65_value.toFixed(2))}
              label="65 and Older"
              risk={data.ep_age65_label}
            />
          </View>
          <View style={{
            border: '1px solid rgb(217, 232, 242)',
            borderRadius: 4,
            padding: 14,
            width: '100%',
            marginLeft: 9,
          }}>
              <Text style={Styles.sectionHeader}>Reported Race or Ethnicity</Text>
              <DataBarGraphPrint
                primaryBrand={primaryBrand}
                label="Asian / Pacific Islander"
                percent={data.race_asian_pi * 100}
              />
              <DataBarGraphPrint
                primaryBrand={primaryBrand}
                label="Black"
                percent={data.race_black * 100}
              />
              <DataBarGraphPrint
                primaryBrand={primaryBrand}
                label="Hispanic"
                percent={data.ethnicity_hispanic * 100}
              />
              <DataBarGraphPrint
                primaryBrand={primaryBrand}
                label="Native American"
                percent={data.race_native_amer * 100}
              />
              <DataBarGraphPrint
                primaryBrand={primaryBrand}
                label="Other"
                percent={data.race_other * 100}
              />
              <DataBarGraphPrint
                primaryBrand={primaryBrand}
                label="White"
                percent={data.race_white * 100}
              />
          </View>
        </View>
      </GeneralPageLayout>
      <GeneralPageLayout
        sectionTypeText="Community Insights"
        sectionTitleText="Key Health Characteristics"
        footerText={uuid}
        textColor={primaryBrand.colors.dark}
        backgroundColor="#FFF"
      >
        <View style={{
          border: '1px solid rgb(217, 232, 242)',
          borderRadius: 4,
          padding: 14,
          width: '100%',
          marginBottom: 18,
        }}>
          <Text style={Styles.sectionHeader}>Health Literacy</Text>
          <DataBlurbPrint
            primaryBrand={primaryBrand}
            value={data.ep_limeng_value.toFixed(2) + '%' }
            label="Speak English 'Less than Well'"
            risk={data.ep_limeng_label}
          />
        </View>
        <View style={{
          border: '1px solid rgb(217, 232, 242)',
          borderRadius: 4,
          padding: 14,
          width: '100%',
          marginBottom: 18,
        }}>
          <Text style={Styles.sectionHeader}>Rates of Preventative Screenings</Text>
          <View style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
          }}>
            <DataBlurbPrint
              primaryBrand={primaryBrand}
              value={data.plcr_cervical_value.toFixed(2) + '%'}
              label="Cervical Screening"
              risk={data.plcr_cervical_label}
            />
            <DataBlurbPrint
              primaryBrand={primaryBrand} 
              value={data.plcr_colon_screen_value.toFixed(2) + '%'}
              label="Colonoscopy"
              risk={data.plcr_colon_screen_label}
            />
            <DataBlurbPrint
              primaryBrand={primaryBrand} 
              value={data.plcr_mammouse_value.toFixed(2) + '%'}
              label="Mammogram"
              risk={data.plcr_mammouse_label}
            />
        </View>
        </View>
        <View style={{
          border: '1px solid rgb(217, 232, 242)',
          borderRadius: 4,
          padding: 14,
          width: '100%',
          marginBottom: 18,
        }}>
          <Text style={Styles.sectionHeader}>Approximate Number of Healthcare providers within {radiusToText(radius)} of {zipCode}</Text>
          <DataBarGraphPrint
            primaryBrand={primaryBrand} 
            label={'Family Medicine'}
            percent={data.NPIs.familyMedicine / data.NPIsMax * 100}
            value={data.NPIs.familyMedicine}
          />
          <DataBarGraphPrint
            primaryBrand={primaryBrand} 
            label="General Practice"
            percent={data.NPIs.generalPractice / data.NPIsMax * 100}
            value={data.NPIs.generalPractice}
          />
          <DataBarGraphPrint
            primaryBrand={primaryBrand} 
            label="Internal Medicine"
            percent={data.NPIs.internalMedicine / data.NPIsMax * 100}
            value={data.NPIs.internalMedicine}
          />
          <DataBarGraphPrint
            primaryBrand={primaryBrand} 
            label="Pediatricians"
            percent={data.NPIs.pediatricians / data.NPIsMax * 100}
            value={data.NPIs.pediatricians}
          />
          <DataBarGraphPrint
            primaryBrand={primaryBrand} 
            label="Psychiatrists/Neurologists"
            percent={data.NPIs.psychiatryNeurology / data.NPIsMax * 100}
            value={data.NPIs.psychiatryNeurology}
          />
          <DataBarGraphPrint
            primaryBrand={primaryBrand} 
            label="Psychologists"
            percent={data.NPIs.psychologist / data.NPIsMax * 100}
            value={data.NPIs.psychologist}
          />
        </View>
      </GeneralPageLayout>
      <GeneralPageLayout
        sectionTypeText="Top AI-Informed Themes"
        backgroundColor={primaryBrand.colors.dark}
        textColor="#FFF"
        footerText={uuid}
        bookmark="Top Vulnerability Themes"
      >
        { _TopThemes }
      </GeneralPageLayout>
    </>
  );
};

export default DataPage;
